import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import {
    Broadcast, Brain, Lightbulb, PenNib, Megaphone, ArrowRight,
    CheckCircle, WarningCircle, Pause, Clock,
} from "@phosphor-icons/react";
import EmptyState from "../components/EmptyState";

/**
 * Workflow — surfaces the intelligence pipeline as a visible journey:
 *   Signals → Analyses → Insights → Content Ideas → Published
 *
 * Each stage shows the volume + a contextual prompt for what to do next.
 * Below the global funnel, each stream is shown with its current "stage"
 * (where it's stuck) and the next recommended action.
 */

const STAGES = [
    { key: "signals_collected",  label: "Signals",      Icon: Broadcast,  description: "Raw conversation data scraped from platforms.", to: "/signals" },
    { key: "analyses_run",       label: "Analyses",     Icon: Brain,      description: "AI synthesis of recurring themes, contradictions, language.", to: "/analyses" },
    { key: "insights_approved",  label: "Insights",     Icon: Lightbulb,  description: "Approved narrative artifacts you stand behind.", to: "/insights", secondaryKey: "insights_suggested", secondaryLabel: "awaiting approval" },
    { key: "content_ideas_total",label: "Content Ideas",Icon: PenNib,     description: "Concrete pieces to publish — carousels, ads, founder videos.", to: "/content-ideas" },
    { key: "published_total",    label: "Published",    Icon: Megaphone,  description: "In production, live, or measuring performance.", to: "/content-ideas?status=Published" },
];

const STAGE_STYLES = {
    needs_signals:           { dot: "bg-muted",       text: "text-muted-foreground", icon: Clock },
    needs_analysis:          { dot: "bg-amber-500",   text: "text-amber-700",         icon: Brain },
    needs_insight_approval:  { dot: "bg-orange-500",  text: "text-orange-700",        icon: Lightbulb },
    needs_content_ideas:     { dot: "bg-primary",     text: "text-primary",           icon: PenNib },
    ready_to_publish:        { dot: "bg-emerald-500", text: "text-emerald-700",       icon: Megaphone },
    warming_up:              { dot: "bg-slate-300",   text: "text-slate-500",         icon: Pause },
};

function StageColumn({ stage, value, secondaryValue, secondaryLabel }) {
    const Icon = stage.Icon;
    return (
        <Link
            to={stage.to}
            data-testid={`stage-${stage.key}`}
            className="group relative flex-1 min-w-[180px] border border-border bg-card p-5 hover:border-primary transition-colors"
        >
            <div className="flex items-center gap-2 mb-3">
                <Icon size={14} weight="duotone" className="text-primary" />
                <div className="overline text-[10px]">{stage.label}</div>
            </div>
            <div className="font-display font-bold text-4xl tabular-nums leading-none mb-3">{value || 0}</div>
            {secondaryValue > 0 && (
                <div className="text-[11px] font-mono uppercase tracking-wider text-accent mb-1.5">
                    + {secondaryValue} {secondaryLabel}
                </div>
            )}
            <p className="text-[12px] text-muted-foreground leading-snug">{stage.description}</p>
            <ArrowRight size={11} className="absolute top-5 right-5 text-muted-foreground group-hover:text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
        </Link>
    );
}

function FunnelArrow() {
    return (
        <div className="flex items-center justify-center text-muted-foreground/40 px-1">
            <ArrowRight size={16} weight="bold" />
        </div>
    );
}

export default function Workflow() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/dashboard/pipeline-status")
            .then((r) => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="px-8 lg:px-14 pt-16 text-sm text-muted-foreground">Loading workflow…</div>;
    if (!data) {
        return (
            <div className="px-8 lg:px-14 pt-16">
                <EmptyState title="Could not load pipeline status" description="Check backend connection and retry." testid="workflow-error" />
            </div>
        );
    }

    const g = data.global || {};
    const streams = data.per_stream || [];

    return (
        <>
            <header className="px-8 lg:px-14 pt-12 pb-6 border-b border-border">
                <div className="overline mb-3">HAEVN Labs</div>
                <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight">Workflow</h1>
                <p className="editorial text-muted-foreground mt-2 max-w-[68ch]">
                    The intelligence pipeline. Where each stream sits in the journey from raw signal to published content — and what to do next at every stage.
                </p>
            </header>

            <main className="px-8 lg:px-14 py-12 max-w-[1400px] mx-auto">
                {/* GLOBAL FUNNEL */}
                <section data-testid="workflow-funnel" className="mb-16">
                    <div className="flex items-baseline gap-3 mb-6">
                        <div className="overline">Global pipeline</div>
                        <div className="h-px bg-border flex-1" />
                        <span className="font-mono text-[11px] text-muted-foreground tracking-wider">
                            funnel
                        </span>
                    </div>
                    <div className="flex flex-wrap items-stretch gap-2">
                        {STAGES.map((s, i) => {
                            const primary = g[s.key];
                            const secondary = s.secondaryKey ? g[s.secondaryKey] : 0;
                            return (
                                <div key={s.key} className="flex items-stretch flex-1 min-w-[180px]">
                                    <StageColumn stage={s} value={primary} secondaryValue={secondary} secondaryLabel={s.secondaryLabel} />
                                    {i < STAGES.length - 1 && <FunnelArrow />}
                                </div>
                            );
                        })}
                    </div>
                </section>

                {/* PER-STREAM JOURNEY */}
                <section data-testid="workflow-per-stream" className="mb-12">
                    <div className="flex items-baseline gap-3 mb-6">
                        <div className="overline">Per-stream journey</div>
                        <div className="h-px bg-border flex-1" />
                        <span className="font-mono text-[11px] text-muted-foreground tracking-wider">
                            {streams.length.toString().padStart(2, "0")} streams
                        </span>
                    </div>
                    <p className="editorial text-[14px] text-muted-foreground max-w-[68ch] mb-6">
                        Each stream gets stuck somewhere in the pipeline. Below shows where — and the single most useful next action.
                    </p>

                    <div className="space-y-3">
                        {streams.map((s) => {
                            const style = STAGE_STYLES[s.stage] || STAGE_STYLES.warming_up;
                            const StageIcon = style.icon;
                            return (
                                <Link
                                    key={s.id}
                                    to={`/streams/${s.id}`}
                                    data-testid={`workflow-stream-${s.id}`}
                                    className="group block border border-border bg-card hover:border-primary transition-colors"
                                >
                                    <div className="grid grid-cols-12 gap-4 items-center p-4">
                                        <div className="col-span-12 md:col-span-4 flex items-center gap-3">
                                            <span className={`w-2 h-2 rounded-full shrink-0 ${style.dot}`} />
                                            <div>
                                                <div className="font-display font-semibold text-base leading-tight">{s.name}</div>
                                                <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mt-0.5">
                                                    {s.active ? "active" : "paused"}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-span-3 md:col-span-1 text-center">
                                            <div className="font-display font-bold tabular-nums">{s.signal_count}</div>
                                            <div className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground">signals</div>
                                        </div>
                                        <div className="col-span-3 md:col-span-1 text-center">
                                            <div className="font-display font-bold tabular-nums">{s.analyses_count}</div>
                                            <div className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground">analyses</div>
                                        </div>
                                        <div className="col-span-3 md:col-span-1 text-center">
                                            <div className="font-display font-bold tabular-nums">
                                                {s.insights_approved}
                                                {s.insights_suggested > 0 && (
                                                    <span className="text-accent text-xs ml-1">+{s.insights_suggested}</span>
                                                )}
                                            </div>
                                            <div className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground">insights</div>
                                        </div>
                                        <div className="col-span-3 md:col-span-1 text-center">
                                            <div className="font-display font-bold tabular-nums">{s.content_ideas_count}</div>
                                            <div className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground">ideas</div>
                                        </div>
                                        <div className="col-span-12 md:col-span-4 flex items-center gap-2">
                                            <StageIcon size={14} weight="duotone" className={`shrink-0 ${style.text}`} />
                                            <span className={`text-[13px] italic ${style.text} leading-snug`}>{s.next_action_hint}</span>
                                            <ArrowRight size={12} className="ml-auto opacity-0 group-hover:opacity-60 text-primary transition-opacity" />
                                        </div>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </section>

                {/* LEGEND */}
                <section className="mt-12 pt-6 border-t border-border max-w-[68ch]">
                    <div className="overline mb-3">Legend</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-[12px]">
                        <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-muted" /><span className="text-muted-foreground">Needs signals — run ingest</span></div>
                        <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-amber-500" /><span className="text-muted-foreground">Needs analysis — synthesize briefing</span></div>
                        <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-orange-500" /><span className="text-muted-foreground">Insights awaiting approval</span></div>
                        <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-primary" /><span className="text-muted-foreground">Approved — generate content ideas</span></div>
                        <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-emerald-500" /><span className="text-muted-foreground">Ready to publish</span></div>
                        <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-slate-300" /><span className="text-muted-foreground">Warming up — building signal</span></div>
                    </div>
                </section>
            </main>
        </>
    );
}
